import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Flex,
  Form,
  Popconfirm,
  Select,
  Skeleton,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ChevronLeft, Pencil } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  type TeamUsersDetail,
  organizationsAccessListOptions,
  organizationsTeamsListOptions,
  organizationsTeamsListQueryKey,
  organizationsTeamsPartialUpdateMutation,
  organizationsTeamsRetrieveOptions,
  organizationsTeamsRetrieveQueryKey,
  organizationsTeamUsersCreateMutation,
  organizationsTeamUsersDestroyMutation,
  organizationsTeamUsersListOptions,
  organizationsTeamUsersListQueryKey
} from 'src/api/main'
import {
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { useDocumentTitle, useHasWorkspacePermissions } from 'src/hooks'
import { paths } from 'src/router/paths'
import { useShowApiErrors, WorkspacePermissionCode } from 'src/utils'
import { composeDisplayName } from 'src/utils/string'

import { orgEntityListCardHeaderStyles } from '../components/workspaceOrgChart/OrgEntityListCard'
import {
  EntityFormFields,
  type EntityFormValues,
  type TeamWithHead
} from '../components/workspaceOrgChart/WorkspaceOrgEntityPanels'

import { useWorkspaceRoute } from './WorkspacePageShell'

export const WorkspaceOrgTeamDetailPage = () => {
  const { t } = useTranslation()

  // The button opens a member's page, which needs `users.view`; this screen is
  // also reachable with `groups.view` alone.
  const mayViewMembers = useHasWorkspacePermissions(
    WorkspacePermissionCode.USERS_VIEW
  )
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const { id: workspaceRouteId = '', teamId = '' } = useParams<{
    id: string
    teamId: string
  }>()
  const navigate = useNavigate()
  const { workspaceId, idValid } = useWorkspaceRoute()
  const numericId = Number(teamId)

  const [editOpen, setEditOpen] = useState(false)
  const [editForm] = Form.useForm<EntityFormValues>()
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [memberForm] = Form.useForm<{ userIds: number[] }>()

  const {
    data: rawTeam,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsTeamsRetrieveOptions({ path: { id: numericId } }),
    enabled: !Number.isNaN(numericId) && numericId > 0
  })

  const team = rawTeam as TeamWithHead | undefined

  useDocumentTitle(team?.name)

  // Team members via team-users endpoint
  const { data: teamUsersPage } = useQuery({
    ...organizationsTeamUsersListOptions({
      query: { team: numericId, per_page: 200 }
    }),
    enabled: !Number.isNaN(numericId) && numericId > 0
  })

  const teamUsers = teamUsersPage?.results ?? []

  // All org members (for the add member select and owner select)
  const { data: membersPage } = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, per_page: 200 }
    }),
    enabled: idValid
  })

  const memberOptions = useMemo(
    () =>
      (membersPage?.results ?? []).map((m) => ({
        label:
          `${m.user.first_name} ${m.user.last_name}`.trim() || m.user.email,
        value: m.user.id
      })),
    [membersPage]
  )

  // Departments for parent select
  const { data: depsPage } = useQuery({
    ...organizationsTeamsListOptions({
      query: { organization: workspaceId, type: ['department'], per_page: 200 }
    }),
    enabled: idValid
  })

  const departmentOptions = useMemo(
    () =>
      (depsPage?.results ?? []).map((d) => ({
        label: d.name,
        value: d.id
      })),
    [depsPage]
  )

  // Members that can be added (not already in team)
  const existingUserIds = useMemo(
    () => new Set(teamUsers.map((tu) => tu.organization_access_id)),
    [teamUsers]
  )

  const addableMemberOptions = useMemo(
    () =>
      (membersPage?.results ?? [])
        .filter((m) => !existingUserIds.has(m.id))
        .map((m) => ({
          label:
            `${m.user.first_name} ${m.user.last_name}`.trim() || m.user.email,
          value: m.user.id
        })),
    [membersPage, existingUserIds]
  )

  // Parent department label
  const parentDepartment = useMemo(() => {
    if (!team?.parent || !depsPage?.results) return null
    return depsPage.results.find((d) => d.id === team.parent)
  }, [team, depsPage])

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: organizationsTeamsRetrieveQueryKey({
          path: { id: numericId }
        })
      }),
      queryClient.invalidateQueries({
        queryKey: organizationsTeamUsersListQueryKey()
      }),
      queryClient.invalidateQueries({
        queryKey: organizationsTeamsListQueryKey()
      })
    ])
  }

  const updateMutation = useMutation({
    ...organizationsTeamsPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.teamUpdated'))
      setEditOpen(false)
      await invalidateAll()
    },
    onError: (e) => showApiErrors(e)
  })

  const addMemberMutation = useMutation({
    ...organizationsTeamUsersCreateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.memberAddedToTeam'))
      setAddMemberOpen(false)
      memberForm.resetFields()
      await invalidateAll()
    },
    onError: (e) => showApiErrors(e)
  })

  const removeMemberMutation = useMutation({
    ...organizationsTeamUsersDestroyMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.memberRemovedFromTeam'))
      await invalidateAll()
    },
    onError: (e) => showApiErrors(e)
  })

  const startEditing = () => {
    if (!team) return
    editForm.setFieldsValue({
      name: team.name,
      abbreviation: team.abbreviation ?? undefined,
      description: team.description ?? undefined,
      head_user: team.head_user ?? undefined,
      parent: team.parent ?? undefined
    })
    setEditOpen(true)
  }

  const ownerLabel = useMemo(() => {
    if (team?.head_user_detail) {
      const d = team.head_user_detail

      return `${d.first_name} ${d.last_name}`.trim() || d.email
    }
    if (team?.top_users?.length) {
      return team.top_users
        .map((u) => `${u.first_name} ${u.last_name}`.trim() || u.email)
        .join(', ')
    }
    return '—'
  }, [team])

  const memberColumns: ColumnsType<TeamUsersDetail> = useMemo(
    () => [
      {
        title: t('workspace.orgChart.members.columns.name'),
        key: 'name',
        fixed: 'left',
        render: (_, row) => {
          const user = (
            row as TeamUsersDetail & {
              user?: { first_name?: string; last_name?: string; email?: string }
            }
          ).user
          if (user) {
            return (
              composeDisplayName(user.first_name, user.last_name, user.email) ||
              '—'
            )
          }
          return '—'
        }
      },
      {
        title: <TableActionColumnTitle />,
        key: 'actions',
        width: 160,
        render: (_, row) => (
          <Flex gap={4}>
            {mayViewMembers && row.organization_access_id != null && (
              <Button
                type="link"
                onClick={() =>
                  navigate(
                    paths.workspaces.membersPerson(
                      workspaceRouteId,
                      String(row.organization_access_id)
                    )
                  )
                }
              >
                {t('workspace.orgChart.actions.view')}
              </Button>
            )}
            <Popconfirm
              title={t('workspace.orgChart.team.removeMemberConfirm')}
              onConfirm={() =>
                removeMemberMutation.mutate({ path: { id: row.id } })
              }
            >
              <Button type="link" danger>
                {t('workspace.orgChart.team.removeMember')}
              </Button>
            </Popconfirm>
          </Flex>
        )
      }
    ],
    [t, navigate, workspaceRouteId, removeMemberMutation, mayViewMembers]
  )

  const backButton = (
    <Button
      type="link"
      icon={<ChevronLeft size={16} />}
      onClick={() => navigate(paths.workspaces.members(workspaceRouteId))}
      style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
    >
      {t('workspace.orgChart.backToMembers')}
    </Button>
  )

  if (isLoading) {
    return (
      <Flex vertical gap={16}>
        {backButton}
        <Card size="small" variant="outlined">
          <Skeleton active />
        </Card>
      </Flex>
    )
  }

  if (isError) {
    return (
      <Flex vertical gap={16}>
        {backButton}
        <QueryErrorAlert onRetry={() => void refetch()} />
      </Flex>
    )
  }

  if (!team) {
    return (
      <Flex vertical gap={16}>
        {backButton}
        <Typography.Title level={5}>
          {t('workspace.orgChart.notFound')}
        </Typography.Title>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      {backButton}

      <Card
        size="small"
        variant="outlined"
        title={team.name}
        styles={orgEntityListCardHeaderStyles}
        extra={
          <Button
            type="text"
            icon={<Pencil size={14} />}
            onClick={startEditing}
          >
            {t('workspace.orgChart.actions.edit')}
          </Button>
        }
      >
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item
            label={t('workspace.orgChart.fields.abbreviation')}
          >
            {team.abbreviation || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.fields.description')}>
            {team.description || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.fields.department')}>
            {parentDepartment ? (
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() =>
                  navigate(
                    paths.workspaces.membersDepartment(
                      workspaceRouteId,
                      String(team.parent)
                    )
                  )
                }
              >
                {parentDepartment.name}
              </Button>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.fields.owner')}>
            {ownerLabel}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.columns.members')}>
            {team.users_count}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <ProTableContainer<TeamUsersDetail>
        showSearch={false}
        rowKey="id"
        columns={memberColumns}
        dataSource={teamUsers}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('workspace.orgChart.memberDetail.teamsEmpty')}
            />
          )
        }}
        leftChildren={
          <Flex vertical gap={2}>
            <Typography.Text strong>
              {t('workspace.orgChart.team.membersTableTitle')}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.orgChart.team.membersTableHint')}
            </Typography.Text>
          </Flex>
        }
        rightChildren={
          <Button
            size="small"
            type="primary"
            onClick={() => {
              memberForm.resetFields()
              setAddMemberOpen(true)
            }}
          >
            {t('workspace.orgChart.team.addMember')}
          </Button>
        }
      />

      {/* Edit drawer */}
      <Drawer
        title={t('workspace.orgChart.teams.editDrawerTitle')}
        size={480}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setEditOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={updateMutation.isPending}
              onClick={() => editForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            updateMutation.mutate({
              path: { id: numericId },
              body: {
                name: values.name,
                abbreviation: values.abbreviation ?? null,
                description: values.description,
                head_user: values.head_user ?? null,
                parent: values.parent ?? null
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            })
          }}
        >
          <EntityFormFields
            t={t}
            memberOptions={memberOptions}
            parentLabel={t('workspace.orgChart.fields.department')}
            parentOptions={departmentOptions}
          />
        </Form>
      </Drawer>

      {/* Add member drawer */}
      <Drawer
        title={t('workspace.orgChart.team.addMemberDrawerTitle')}
        size={480}
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setAddMemberOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              type="primary"
              loading={addMemberMutation.isPending}
              onClick={() => memberForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={memberForm}
          layout="vertical"
          onFinish={(values) => {
            for (const userId of values.userIds) {
              addMemberMutation.mutate({
                body: {
                  user: userId,
                  team: numericId
                }
              })
            }
          }}
        >
          <Form.Item
            name="userIds"
            label={t('workspace.orgChart.team.selectMembers')}
            rules={[{ required: true, message: t('required_field_error') }]}
          >
            <Select
              mode="multiple"
              showSearch={{ optionFilterProp: 'label' }}
              placeholder={t(
                'workspace.orgChart.team.selectMembersPlaceholder'
              )}
              options={addableMemberOptions}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </Flex>
  )
}
